// The flame under a king that is flying itself in.
//
// Three cones in the base rather than one under the middle: one reads as a
// rocket the piece is sitting on, three read as thrusters built into it, which
// is the difference between the king being cargo and the king being the craft.
//
// Unlit and additive on purpose. A `MeshStandardMaterial` flame is lit by the
// board's key light, so it goes *darker* on the shadowed side of the piece it
// is supposed to be lighting — which is the one thing a flame may never do.

import {
  AdditiveBlending,
  ConeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial
} from "three";

import { PALETTE } from "./palette";

/** How far out from the piece's centre the three nozzles sit. */
const NOZZLE_RADIUS = 0.16;
/** The flame's width at the nozzle, and its length at full throttle. */
const FLAME_WIDTH = 0.13;
const FLAME_LENGTH = 0.62;
const NOZZLES = 3;

/**
 * How much the flame's length wanders, and how fast.
 *
 * A flame drawn at a constant length is a cone, and the eye reads a cone as
 * geometry. The flicker is what makes it burn — small, because at this pitch a
 * large one reads as the model itself jittering.
 */
const FLICKER = 0.16;
const FLICKER_HZ = 34;

/**
 * The three flames, as a group to hang off the piece that is landing.
 *
 * Owns its geometry and material and is asked to dispose of them, because a
 * cinematic that runs once a run must not leave anything behind: this is
 * created when a king arrives and thrown away a second and a half later.
 */
export class Thrusters {
  readonly group = new Group();
  private geometry: ConeGeometry;
  private material: MeshBasicMaterial;
  private cones: Mesh[] = [];

  constructor() {
    // Apex down: the cone's origin is its middle and its point is at +Y in
    // three's own convention, so it is turned over and hung below the base.
    this.geometry = new ConeGeometry(FLAME_WIDTH, FLAME_LENGTH, 10, 1, true);
    this.material = new MeshBasicMaterial({
      color: PALETTE.thruster,
      transparent: true,
      opacity: 0.9,
      blending: AdditiveBlending,
      // A flame does not occlude and does not want to be occluded by the piece
      // it is under — the nozzles are inside the model's own silhouette.
      depthWrite: false
    });

    for (let i = 0; i < NOZZLES; i++) {
      const cone = new Mesh(this.geometry, this.material);
      const angle = (i / NOZZLES) * Math.PI * 2;
      cone.position.set(
        Math.cos(angle) * NOZZLE_RADIUS,
        -FLAME_LENGTH / 2,
        Math.sin(angle) * NOZZLE_RADIUS
      );
      cone.rotation.x = Math.PI;
      // Never a shadow caster: a flame casting one is a black stripe across
      // the board directly under the thing it is lighting up.
      cone.castShadow = false;
      cone.receiveShadow = false;
      this.cones.push(cone);
      this.group.add(cone);
    }
    this.group.visible = false;
  }

  /**
   * Burn this hard, at this moment.
   *
   * Length rather than opacity carries the throttle: a flame that fades in at
   * full length reads as a decal appearing, and one that grows out of the
   * nozzle reads as an engine spooling up. Each nozzle flickers on its own
   * phase, or the three move as one object and the whole thing pulses.
   */
  set(throttle: number, elapsed: number): void {
    this.group.visible = throttle > 0.01;
    if (!this.group.visible) return;

    for (let i = 0; i < this.cones.length; i++) {
      const flicker = 1 + Math.sin((elapsed * FLICKER_HZ) + i * 2.1) * FLICKER;
      const length = Math.max(0.001, throttle * flicker);
      const cone = this.cones[i];
      // Scaled about its own middle, so it has to slide down by half of
      // whatever it grew, or the flame reaches up into the piece as it opens.
      cone.scale.set(1, length, 1);
      cone.position.y = (-FLAME_LENGTH / 2) * length;
    }
    this.material.opacity = 0.55 + 0.35 * throttle;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    this.cones = [];
  }
}
